import { createContext, useContext, useEffect, type ReactNode } from 'react';
import { useElefaCall, type UseElefaCallReturn } from '@/hooks/useElefaCall';
import { IncomingCallModal } from '@/components/connect/IncomingCallModal';
import { InternalCallScreen } from '@/components/connect/InternalCallScreen';
import { setCallActive } from '@/lib/call-audio-coordinator';

const ElefaCallContext = createContext<UseElefaCallReturn | null>(null);

/**
 * Global provider that mounts `useElefaCall` once at the app level.
 * Renders incoming-call and active-call overlays globally so they
 * appear regardless of which page the user is viewing.
 */
export function ElefaCallProvider({ children }: { children: ReactNode }) {
  const elefaCall = useElefaCall();

  const {
    callId,
    callerId,
    calleeId,
    callerName,
    callerAvatar,
    status,
    isVideo,
    isIncoming,
    duration,
    localStream,
    remoteStream,
    isMuted,
    isVideoEnabled,
    answerCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleVideo,
    isSpeakerOn,
    toggleSpeaker,
    isOnHold,
    isRemoteOnHold,
    toggleHold,
    flipCamera,
  } = elefaCall;

  // Notify the call-audio coordinator (Radio etc.) of call activity.
  useEffect(() => {
    const active = status === 'ringing' || status === 'connecting' || status === 'connected';
    setCallActive(active);
  }, [status]);

  const handleAnswerCall = () => {
    if (callId) answerCall(callId);
  };

  const handleRejectCall = () => {
    if (callId) rejectCall(callId);
  };

  return (
    <ElefaCallContext.Provider value={elefaCall}>
      {children}

      {/* Global incoming-call overlay */}
      <IncomingCallModal
        isOpen={status === 'ringing' && isIncoming}
        callerName={callerName || 'Unknown'}
        callerAvatar={callerAvatar}
        isVideo={isVideo}
        onAnswer={handleAnswerCall}
        onReject={handleRejectCall}
      />

      {/* Global active-call overlay */}
      {(status === 'connecting' || status === 'connected' || (status === 'ringing' && !isIncoming)) && (
        <InternalCallScreen
          callerName={callerName || 'Unknown'}
          callerAvatar={callerAvatar}
          status={status}
          isVideo={isVideo}
          isIncoming={isIncoming}
          duration={duration}
          localStream={localStream}
          remoteStream={remoteStream}
          isMuted={isMuted}
          isVideoEnabled={isVideoEnabled}
          onEndCall={endCall}
          onToggleMute={toggleMute}
          onToggleVideo={toggleVideo}
          calleeId={isIncoming ? callerId : calleeId}
          isSpeakerOn={isSpeakerOn}
          onToggleSpeaker={toggleSpeaker}
          isOnHold={isOnHold}
          isRemoteOnHold={isRemoteOnHold}
          onToggleHold={toggleHold}
          onFlipCamera={flipCamera}
        />
      )}
    </ElefaCallContext.Provider>
  );
}

/**
 * Hook to consume the global ElefaCall state.
 * Must be used inside `<ElefaCallProvider>`.
 */
export function useElefaCallContext(): UseElefaCallReturn {
  const ctx = useContext(ElefaCallContext);
  if (!ctx) {
    throw new Error('Call service is still starting. Please try again in a moment.');
  }
  return ctx;
}
